import React, { useState, useCallback } from 'react';
import { Download, FileSpreadsheet, FileText, Globe, Link2, Settings, Webhook } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { newSelect as Select } from '../ui/newSelect';

const formatOptions = [
  { value: 'csv', label: 'CSV (Spreadsheet)' },
  { value: 'pdf', label: 'PDF (Report)' },
];

const typeOptions = [
  { value: 'usage_data', label: 'Usage Data Export' },
  { value: 'ghg_report', label: 'GHG Report' },
  { value: 'audit_trail', label: 'Audit Trail' },
];

const exportCards = [
  {
    id: 'csv',
    icon: FileSpreadsheet,
    title: 'CSV Data Export',
    description: 'Download your usage data as a spreadsheet with emission calculations, quality tiers, and factor sources.',
    format: 'csv',
    type: 'usage_data',
    available: true,
  },
  {
    id: 'pdf',
    icon: FileText,
    title: 'PDF GHG Report',
    description: 'Generate a formatted PDF report with executive summary, scope totals, quality assessment, and methodology statement.',
    format: 'pdf',
    type: 'ghg_report',
    available: true,
  },
  {
    id: 'audit',
    icon: FileSpreadsheet,
    title: 'Audit Trail Export',
    description: 'Export the full audit trail of AI decisions, user overrides, and approvals as a CSV.',
    format: 'csv',
    type: 'audit_trail',
    available: true,
  },
  {
    id: 'webhook',
    icon: Webhook,
    title: 'API Webhook',
    description: 'Configure webhooks to automatically send data to external systems when imports complete.',
    available: false,
    comingSoon: true,
  },
  {
    id: 'xero',
    icon: Link2,
    title: 'Xero Integration',
    description: 'Sync emission data with your Xero accounting software.',
    available: false,
    comingSoon: true,
  },
  {
    id: 'sage',
    icon: Link2,
    title: 'Sage Integration',
    description: 'Sync emission data with Sage accounting software.',
    available: false,
    comingSoon: true,
  },
];

/**
 * Export and integration hub (Screen 24).
 * CSV/PDF exports, API webhook placeholders, accounting integration placeholders.
 * @param {object} props
 * @param {Function} [props.onExport] - handler for export { format, type, filters }
 * @param {Array} [props.pastExports] - list of past exports
 */
const ExportHub = ({ onExport, pastExports = [] }) => {
  const [exporting, setExporting] = useState({});

  const handleExport = useCallback(async (card) => {
    if (!card.available || !onExport) return;
    setExporting((prev) => ({ ...prev, [card.id]: true }));
    try {
      await onExport({ format: card.format, type: card.type });
    } finally {
      setExporting((prev) => ({ ...prev, [card.id]: false }));
    }
  }, [onExport]);

  const handleCsvDownload = useCallback(() => {
    // Frontend-only CSV export placeholder
  }, []);

  return (
    <div className='flex flex-col gap-6 p-6'>
      <div>
        <h1 className='text-2xl font-bold text-foreground flex items-center gap-2'>
          <Download size={24} strokeWidth={1.5} />
          Export & Integrations
        </h1>
        <p className='text-muted-foreground mt-1'>Export your data in multiple formats or connect with external systems</p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {exportCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.id}
              className={`bg-card transition-all ${card.available ? 'hover:border-primary/50' : 'opacity-60'}`}
            >
              <CardContent className='p-6'>
                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-lg bg-primary/10'>
                    <Icon size={20} className='text-primary' strokeWidth={1.5} />
                  </div>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <h3 className='font-semibold text-foreground'>{card.title}</h3>
                      {card.comingSoon && (
                        <Badge className='bg-muted text-muted-foreground text-xs'>Coming Soon</Badge>
                      )}
                    </div>
                    <p className='text-sm text-muted-foreground mt-1'>{card.description}</p>
                    {card.available && (
                      <Button
                        variant='outline'
                        size='sm'
                        className='mt-3'
                        onClick={() => handleExport(card)}
                        loading={exporting[card.id]}
                      >
                        <Download size={14} strokeWidth={1.5} />
                        Export
                      </Button>
                    )}
                    {card.comingSoon && (
                      <Button variant='ghost' size='sm' className='mt-3' disabled>
                        <Settings size={14} strokeWidth={1.5} />
                        Configure
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {pastExports.length > 0 && (
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-lg'>Recent Exports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col gap-2'>
              {pastExports.map((exp) => (
                <div key={exp.id || exp.exportId} className='flex items-center justify-between p-3 bg-muted/30 rounded'>
                  <div>
                    <p className='text-sm font-medium'>{exp.type} ({exp.format?.toUpperCase()})</p>
                    <p className='text-xs text-muted-foreground'>
                      {exp.createdAt ? new Date(exp.createdAt).toLocaleString() : ''}
                    </p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Badge className={
                      exp.status === 'ready' ? 'bg-success-bg text-success' :
                      exp.status === 'processing' ? 'bg-info-bg text-info' :
                      'bg-muted text-muted-foreground'
                    }>
                      {exp.status}
                    </Badge>
                    {exp.status === 'ready' && (
                      <Button variant='ghost' size='sm'>
                        <Download size={14} strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExportHub;
